import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Widgets from "../../../widgets";
import PatientPersonalInfo from "../../patientFullView/patientPersonalInfo";
import PatientHistoryItem from "./PatientHistoryItem";
import { getPatientHistory } from "../../../modules/NursignCare/ManageRequest/manageRequestThunk";
import strings from "../../../utils/strings";
import "./PatientHistoryDetails.css";

const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
};

const formatTime = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    let h = d.getHours();
    const min = String(d.getMinutes()).padStart(2, "0");
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${h}:${min} ${ampm}`;
};

const PatientHistoryDetails = () => {
    const { t } = useTranslation();
    const { hnNumber } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { patientHistoryData } = useSelector((state) => state.manageRequest);
    const [expandedItems, setExpandedItems] = useState({});

    const breadcrumbs = ["nursingCare", "patientHistory", "patientDetails"];
    const paths = [
        { path: strings.navigation.nursingCareManageService },
        {
            path: strings.navigation.patientHistory,
            onClick: () => navigate(strings.navigation.patientHistory),
        },
        { path: strings.navigation.patientHistoryDetails },
    ];

    useEffect(() => {
        // Only fetch if data isn't already loaded for this HN number.
        // SearchPatientHistory already fetched it before navigating here.
        if (!patientHistoryData || patientHistoryData.patient?.hnNumber !== hnNumber) {
            dispatch(getPatientHistory({ hnNumber }));
        }
    }, [hnNumber]);

    useEffect(() => {
        if (patientHistoryData?.sessions?.data?.length > 0) {
            setExpandedItems({ [patientHistoryData.sessions.data[0]._id]: true });
        }
    }, [patientHistoryData]);

    const handleToggle = (itemId) => {
        setExpandedItems((prev) => ({
            ...prev,
            [itemId]: !prev[itemId],
        }));
    };

    if (!patientHistoryData) {
        return (
            <div style={{ position: "fixed", inset: 0, zIndex: 9999 }}>
                <Widgets.LoadingContainer />
            </div>
        );
    }

    const { patient, addresses, sessions } = patientHistoryData;

    // Map API patient fields to the shape PatientPersonalInfo expects
    const patientDetails = {
        firstName: patient.firstName,
        lastName: patient.lastName,
        hnNumber: patient.hnNumber,
        gender: patient.gender,
        dateOfBirth: patient.dateOfBirth,
        countryCode: `+${patient.countryCode}`,
        phoneNumber: patient.phoneNumber,
        email: patient.email,
        insuranceCategory: patient.insuranceCategory,
        profilePhoto: patient.profilePhoto,
    };

    // Build readable address string from the first address entry
    const addr = addresses?.[0];
    const addressString = addr
        ? [addr.street, addr.ward, addr.city, addr.province, addr.postalCode].filter(Boolean).join(", ")
        : "-";

    // Transform API sessions into the shape PatientHistoryItem expects
    const historyItems = (sessions?.data || []).map((session) => {
        const nurseNames = (session.nurse || [])
            .map((n) => `${n.firstName?.en || n.firstName || ""} ${n.lastName?.en || n.lastName || ""}`.trim())
            .join(", ");
        const time = formatTime(session.startTime);
        const serviceLabel = session.serviceItem?.title?.en || "-";
        const serviceType = nurseNames
            ? `${serviceLabel} - ${nurseNames} (${time})`
            : `${serviceLabel} (${time})`;

        return {
            id: session._id,
            date: formatDate(session.startTime),
            serviceType,
            woundCare: {
                images: session.medicalUploadedImages || [],
                observations: session.checkoutNotes || "-",
                notes: session.notes || "-",
            },
        };
    });

    // Date range: sort sessions by startTime to get earliest → latest
    const sorted = [...(sessions?.data || [])].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    const fromDate = formatDate(sorted[0]?.startTime);
    const toDate = formatDate(sorted[sorted.length - 1]?.startTime);

    return (
        <div className="phd-container">
            <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />
            <div className="department-heading">
                <div>{t("patientHistoryViewer")}</div>
            </div>

            {/* Patient Personal Information */}
            <PatientPersonalInfo data={patientDetails} />

            {/* Patient Address */}
            <div className="phd-address-section">
                <p className="phd-section-title">{t("patientAddress")}</p>
                <div className="phd-address-content">
                    <p className="phd-address-text">{addressString}</p>
                </div>
            </div>

            {/* Patient History */}
            <div className="phd-history-section">
                <div className="phd-history-header">
                    <p className="phd-section-title">{t("patientHistory")}</p>
                    <div className="phd-date-range">
                        <span className="phd-date-label">{t("fromDate")}:</span>
                        <span className="phd-date-value">{fromDate}</span>
                        <span className="phd-date-separator">-</span>
                        <span className="phd-date-label">{t("toDate")}:</span>
                        <span className="phd-date-value">{toDate}</span>
                    </div>
                </div>
                <div className="phd-history-list">
                    {historyItems.map((item) => (
                        <PatientHistoryItem
                            key={item.id}
                            historyItem={item}
                            isExpanded={expandedItems[item.id]}
                            onToggle={() => handleToggle(item.id)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default PatientHistoryDetails;
